import type { FleetCompositionBundle } from '../../services/fleetCompositionService';
import { fuelLabel, vehicleCategoryLabel } from '../../services/fleetCompositionService';
import type { FleetCompositionMetrics } from '../../services/fleetCompositionService';
import { buildTrendSentence } from './optionalClauseTokens';
import { isFiniteNumber } from './narrativeFormatting';

export type FleetScope = 'LDV' | 'all';

function vehiclePopulation(v: unknown): number | null {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

type PopAgg = {
  sum: number;
  validRowCount: number;
  missingRowCount: number;
  coverageComplete: boolean;
};

function aggregatePopulation(
  rows: { vehicle_population?: number }[]
): PopAgg {
  let sum = 0;
  let validRowCount = 0;
  let missingRowCount = 0;
  for (const r of rows) {
    const pop = vehiclePopulation(r.vehicle_population);
    if (pop === null) {
      missingRowCount += 1;
      continue;
    }
    validRowCount += 1;
    sum += pop;
  }
  return {
    sum,
    validRowCount,
    missingRowCount,
    coverageComplete: validRowCount > 0 && missingRowCount === 0,
  };
}

function rowsForScope(bundle: FleetCompositionBundle, year: number, scope: FleetScope) {
  return bundle.rows.filter(
    (r) => r.year === year && (scope === 'all' || r.class === 'LDV')
  );
}

function pluginPop(rows: { fuel_type?: string; vehicle_population?: number }[]) {
  let sum = 0;
  for (const r of rows) {
    if (r.fuel_type !== 'Electric' && r.fuel_type !== 'PluginHybrid') continue;
    const pop = vehiclePopulation(r.vehicle_population);
    if (pop === null) return null;
    sum += pop;
  }
  return sum;
}

function dominantFuel(rows: { fuel_type?: string; vehicle_population?: number }[]) {
  const byFuel: Record<string, number> = {};
  let missing = 0;
  for (const r of rows) {
    const pop = vehiclePopulation(r.vehicle_population);
    if (pop === null) {
      missing += 1;
      continue;
    }
    const f = r.fuel_type || 'Other';
    byFuel[f] = (byFuel[f] || 0) + pop;
  }
  const total = Object.values(byFuel).reduce((a, b) => a + b, 0);
  if (total <= 0 || missing > 0) return null;
  let best = '';
  let bestPop = 0;
  for (const [fuel, pop] of Object.entries(byFuel)) {
    if (pop > bestPop) {
      bestPop = pop;
      best = fuel;
    }
  }
  return { label: fuelLabel(best), share: (bestPop / total) * 100, total };
}

function dominantCategory(rows: { vehicle_category?: string; vehicle_population?: number }[]) {
  const byCat: Record<string, number> = {};
  let missing = 0;
  for (const r of rows) {
    const pop = vehiclePopulation(r.vehicle_population);
    if (pop === null) {
      missing += 1;
      continue;
    }
    const c = r.vehicle_category || 'Other';
    byCat[c] = (byCat[c] || 0) + pop;
  }
  const total = Object.values(byCat).reduce((a, b) => a + b, 0);
  if (total <= 0 || missing > 0) return null;
  const ranked = Object.entries(byCat).sort((a, b) => b[1] - a[1]);
  const [topCat, topPop] = ranked[0];
  const second = ranked[1];
  return {
    dominantLabel: vehicleCategoryLabel(topCat),
    dominantShare: (topPop / total) * 100,
    secondLabel: second ? vehicleCategoryLabel(second[0]) : null,
    secondShare: second ? (second[1] / total) * 100 : null,
    total,
  };
}

function ldvHdvShares(rows: { class?: string; vehicle_population?: number }[]) {
  const agg = aggregatePopulation(rows);
  if (!agg.coverageComplete || agg.sum <= 0) return null;
  let ldv = 0;
  let hdv = 0;
  for (const r of rows) {
    const pop = vehiclePopulation(r.vehicle_population);
    if (pop === null) return null;
    if (r.class === 'LDV') ldv += pop;
    if (r.class === 'HDV') hdv += pop;
  }
  const total = ldv + hdv;
  if (total <= 0) return null;
  return { ldvShare: (ldv / total) * 100, hdvShare: (hdv / total) * 100, total };
}

export function buildFleetFuelFacts(
  locationName: string,
  bundle: FleetCompositionBundle | null,
  scope: FleetScope
) {
  const metrics = bundle?.metrics;
  if (!bundle || !metrics) return { locationName, latestYear: null };
  const year = metrics.latestYear;
  const baseline = metrics.baselineYear;
  const rows = rowsForScope(bundle, year, scope);
  const baseRows = rowsForScope(bundle, baseline, scope);
  const dom = dominantFuel(rows);
  const total = dom?.total ?? null;
  const plugin = pluginPop(rows);
  const pluginShare =
    total != null && plugin != null && total > 0 ? (plugin / total) * 100 : null;
  const baseDom = dominantFuel(baseRows);
  const baseTotal = baseDom?.total ?? null;
  const basePlugin = pluginPop(baseRows);
  const basePluginShare =
    baseTotal != null && basePlugin != null && baseTotal > 0
      ? (basePlugin / baseTotal) * 100
      : null;
  const pluginSharePointChange =
    pluginShare != null && basePluginShare != null ? pluginShare - basePluginShare : null;
  const trendSentence =
    isFiniteNumber(baseline) && isFiniteNumber(pluginSharePointChange)
      ? Math.abs(pluginSharePointChange) < 0.05
        ? `, with little change in plug-in share since ${baseline}`
        : pluginSharePointChange > 0
          ? `, an increase of ${pluginSharePointChange.toFixed(1)} percentage points in plug-in share since ${baseline}`
          : `, a decrease of ${Math.abs(pluginSharePointChange).toFixed(1)} percentage points in plug-in share since ${baseline}`
      : '';

  return {
    locationName,
    latestYear: year,
    dominantFleetCategory: dom?.label ?? null,
    dominantShare: dom != null ? Number(dom.share.toFixed(1)) : null,
    pluginShare: pluginShare != null ? Number(pluginShare.toFixed(1)) : null,
    pluginVehicleCount: plugin,
    displayedFleetTotal: total,
    pluginSharePointChange,
    trendSentence,
    validRowCount: aggregatePopulation(rows).validRowCount,
    missingRowCount: aggregatePopulation(rows).missingRowCount,
    coverageComplete: aggregatePopulation(rows).coverageComplete,
  };
}

export function buildFleetClassCategoryFacts(locationName: string, bundle: FleetCompositionBundle | null) {
  const metrics = bundle?.metrics;
  if (!bundle || !metrics) return { locationName, latestYear: null };
  const year = metrics.latestYear;
  const rows = bundle.rows.filter((r) => r.year === year);
  const dom = dominantCategory(rows);
  const secondaryClassSentence =
    dom?.secondLabel != null && dom.secondShare != null
      ? `${dom.secondLabel} was the second-largest category at ${dom.secondShare.toFixed(1)}%. `
      : '';
  return {
    locationName,
    latestYear: year,
    dominantClassLabel: dom?.dominantLabel ?? null,
    dominantClassShare: dom != null ? Number(dom.dominantShare.toFixed(1)) : null,
    secondaryClassSentence,
    classTrendSentence: '',
    displayedFleetTotal: dom?.total ?? null,
  };
}

export function buildFleetDutyClassFacts(locationName: string, bundle: FleetCompositionBundle | null) {
  const metrics = bundle?.metrics;
  if (!bundle || !metrics) return { locationName, latestYear: null };
  const year = metrics.latestYear;
  const rows = bundle.rows.filter((r) => r.year === year);
  const shares = ldvHdvShares(rows);
  return {
    locationName,
    latestYear: year,
    ldvShare: shares != null ? Number(shares.ldvShare.toFixed(1)) : null,
    hdvShare: shares != null ? Number(shares.hdvShare.toFixed(1)) : null,
    dutyClassTrendSentence: '',
    displayedFleetTotal: shares?.total ?? null,
  };
}

export function buildFleetIntensityFacts(
  locationName: string,
  _bundle: FleetCompositionBundle | null,
  metrics: FleetCompositionMetrics | null
) {
  if (!metrics) return { locationName, latestYear: null };
  const intensityTrendSentence = buildTrendSentence(
    metrics.ldvGPerMilePctChange,
    metrics.baselineYear
  );
  const intensityComparisonSentence =
    metrics.caLdvGPerMileLatest != null && isFiniteNumber(metrics.ldvGPerMileLatest)
      ? ` California’s comparable modeled LDV intensity was ${metrics.caLdvGPerMileLatest.toFixed(1)} g CO2e per mile (${metrics.latestYear}).`
      : '';
  return {
    locationName,
    latestYear: metrics.latestYear,
    ldvIntensity: Number(metrics.ldvGPerMileLatest.toFixed(1)),
    intensityTrendSentence,
    intensityComparisonSentence,
  };
}
